import React, { useMemo } from 'react';
import { Autocomplete, TextField, Box } from '@mui/material';
import { useAsyncPaginatedCatalog } from '../../../../hooks/useAsyncPaginatedCatalog';
import { fetchActiveSuppliersPage } from '../../../../utils/catalogPageFetchers';

export interface SupplierSelectorProps {
  /** Habilita la carga paginada de proveedores (solo cuando el panel está abierto en este paso). */
  isActive: boolean;
  /** Proveedor del registro en edición (para mostrar etiqueta aunque no esté en la primera página). */
  editPin?: { id: number; name: string } | null;
  selectedSupplierId: string;
  /** Muestra error en rojo solo tras validación (p. ej. guardar borrador sin proveedor). */
  showError?: boolean;
  onChange: (supplierId: string) => void;
  disabled?: boolean;
}

const SupplierSelector: React.FC<SupplierSelectorProps> = ({
  isActive,
  editPin = null,
  selectedSupplierId,
  showError = false,
  onChange,
  disabled = false,
}) => {
  const supplierPins = useMemo(() => {
    const id = Number(selectedSupplierId);
    if (!id || Number.isNaN(id)) return [];
    if (editPin && editPin.id === id) {
      return [{ id: editPin.id, name: editPin.name }];
    }
    return [{ id, name: `Proveedor #${id}` }];
  }, [selectedSupplierId, editPin]);

  const supplierCatalog = useAsyncPaginatedCatalog({
    isActive,
    fetchPage: fetchActiveSuppliersPage,
    pageSize: 10,
    pinItems: supplierPins,
  });

  const selectedSupplier =
    supplierCatalog.displayOptions.find((s) => s.id.toString() === selectedSupplierId) || null;

  const isSupplierMissing = !selectedSupplierId || selectedSupplierId === '';
  const showSupplierFieldError = showError && isSupplierMissing && !disabled;

  return (
    <Box sx={{ maxWidth: '420px', marginBottom: '18px' }}>
      <Autocomplete
        options={supplierCatalog.displayOptions}
        filterOptions={supplierCatalog.filterOptions}
        loading={supplierCatalog.loading}
        openOnFocus
        onOpen={supplierCatalog.onOpen}
        onInputChange={supplierCatalog.onInputChange}
        getOptionLabel={(option) => option.name}
        value={selectedSupplier || null}
        onChange={(_, newValue) => {
          onChange(newValue ? newValue.id.toString() : '');
        }}
        disabled={disabled}
        ListboxProps={supplierCatalog.listboxProps}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Proveedor"
            placeholder="Seleccionar proveedor"
            required
            error={showSupplierFieldError}
            helperText={showSupplierFieldError ? 'Debe seleccionar un proveedor' : ''}
          />
        )}
        noOptionsText="No hay proveedores disponibles"
      />
    </Box>
  );
};

export default SupplierSelector;
